import { Group, Stack } from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import type { EnhancedNotification } from '../../types';

interface ITableSchemaChangeContentProps {
	selected: EnhancedNotification;
}

export function TableSchemaChangeContent({
	selected,
}: ITableSchemaChangeContentProps) {
	const schemaChange = useMemo(() => {
		const meta = selected.metadata as Record<string, any> | null | undefined;
		return meta?.schema_change ?? {};
	}, [selected.metadata]);

	const addedColumns = Array.isArray(schemaChange.new_columns)
		? (schemaChange.new_columns as string[])
		: [];

	const removedColumns = Array.isArray(schemaChange.deleted_columns)
		? (schemaChange.deleted_columns as string[])
		: [];

	// Do not render if nothing meaningful
	if (isEmpty(addedColumns) && isEmpty(removedColumns)) {
		return null;
	}

	const renderColumnRow = (name: string, isAdded: boolean) => (
		<Group key={name} spacing={4} align="center">
			<Icon
				name={isAdded ? 'plus' : 'minus'}
				color={isAdded ? 'icon/success/default' : 'icon/critical/default'}
				style={{ flexShrink: 0 }}
			/>
			<Text size="sm">{name}</Text>
		</Group>
	);

	return (
		<Stack spacing="xl">
			{!isEmpty(addedColumns) && (
				<Stack spacing="xs">
					<Text size="sm" color="text/secondary/default">
						Added columns
					</Text>
					<Stack spacing={4}>
						{addedColumns.map((col) => renderColumnRow(col, true))}
					</Stack>
				</Stack>
			)}

			{!isEmpty(removedColumns) && (
				<Stack spacing="xs">
					<Text size="sm" color="text/secondary/default">
						Removed columns
					</Text>
					<Stack spacing={4}>
						{removedColumns.map((col) => renderColumnRow(col, false))}
					</Stack>
				</Stack>
			)}
		</Stack>
	);
}
